"use client";

import { Clock } from "lucide-react";
import { EditPanel } from "@/components/ui/edit-panel";
import { FIELD_CLASSES } from "@/components/ui/select";
import { formatDuration } from "@/lib/units";

const HOUR_MS = 3600 * 1000;

// Format an epoch-ms instant as a `datetime-local` value in the user's local
// timezone (YYYY-MM-DDTHH:mm:ss), which the input expects.
function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

function parseLocalInput(text: string): number | null {
  const ms = new Date(text).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function formatShift(ms: number): string {
  if (!ms) return "No change";
  return `${ms > 0 ? "+" : "−"}${formatDuration(Math.abs(ms))}`;
}

export function TimeAdjuster({
  startMs,
  value,
  onChange,
}: {
  startMs?: number;
  value: number;
  onChange: (shiftMs: number) => void;
}) {
  if (startMs === undefined) return null;

  const newStart = startMs + value;

  const onInput = (text: string) => {
    const parsed = parseLocalInput(text);
    if (parsed === null) return;
    onChange(parsed - startMs);
  };

  return (
    <EditPanel
      icon={Clock}
      title="Adjust date & time"
      onReset={value !== 0 ? () => onChange(0) : undefined}
    >
      <p className="text-sm text-muted-foreground mb-4">
        Fix a wrong time zone or a drifted device clock. Every timestamp shifts together.
      </p>

      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          New start time
        </span>
        <input
          type="datetime-local"
          step={1}
          value={toLocalInput(newStart)}
          onChange={(e) => onInput(e.target.value)}
          className={FIELD_CLASSES}
        />
      </label>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {[-1, +1].map((h) => (
          <button
            key={h}
            type="button"
            onClick={() => onChange(value + h * HOUR_MS)}
            className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:border-primary/40 hover:text-primary transition-colors"
          >
            {h > 0 ? "+1 hour" : "−1 hour"}
          </button>
        ))}
        <span className="ml-auto text-sm tabular-nums text-muted-foreground">
          Shift: <span className="font-medium text-foreground">{formatShift(value)}</span>
        </span>
      </div>
    </EditPanel>
  );
}
